<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("custom_timezones.title", 2)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Tenant -->
          <validation-provider
            #default="validationContext"
            name="id_tenant"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('tenants.title', 1)"
              label-for="id_tenant"
            >
              <v-select
                v-model="itemData.id_tenant"
                :options="tenantOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                input-id="id_tenant"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Timezone -->
          <validation-provider
            #default="validationContext"
            name="timezone"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              label="Timezone"
              label-for="timezone"
            >
              <v-select
                label="name"
                v-model="itemData.id_timezone"
                :options="timezoneOptions"
                :clearable="true"
                :reduce="(val) => val.id"
                input-id="id_tenant"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider
            #default="validationContext"
            :name="$t('companies.description')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('companies.description')"
              label-for="description"
            >
              <b-form-input
                id="description"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <p>{{ $t('custom_timezones.dts') }}</p>

          <!-- Start -->
          <validation-provider
            #default="validationContext"
            name="start"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('custom_timezones.start', 1)"
              label-for="start"
            >
                <b-row>
                  <b-col>
                    <label for="month">
                      {{ $t('custom_timezones.month') }}
                    </label>
                    <v-select
                      v-model="itemData.start_dst.month"
                      :options="months"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="month"
                    />
                  </b-col>
                  <b-col>
                    <label for="week">
                      {{ $t('custom_timezones.week') }}
                    </label>
                    <v-select
                      v-model="itemData.start_dst.week"
                      :options="weeks"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="week"
                    />
                  </b-col>
                  <b-col>
                    <label for="day">
                      {{ $t('custom_timezones.day') }}
                    </label>
                    <v-select
                      v-model="itemData.start_dst.day"
                      :options="days"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="day"
                    />
                  </b-col>
                </b-row>
            </b-form-group>
          </validation-provider>

           <validation-provider
            #default="validationContext"
            name="end"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('custom_timezones.end', 1)"
              label-for="end"
            >
                <b-row>
                  <b-col>
                    <label for="month">
                      {{ $t('custom_timezones.month') }}
                    </label>
                    <v-select
                      v-model="itemData.end_dst.month"
                      :options="months"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="month"
                    />
                  </b-col>
                  <b-col>
                    <label for="week">
                      {{ $t('custom_timezones.week') }}
                    </label>
                    <v-select
                      v-model="itemData.end_dst.week"
                      :options="weeks"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="week"
                    />
                  </b-col>
                  <b-col>
                    <label for="day">
                      {{ $t('custom_timezones.day') }}
                    </label>
                    <v-select
                      v-model="itemData.end_dst.day"
                      :options="days"
                      :clearable="false"
                      :reduce="(val) => val.value"
                      input-id="day"
                    />
                  </b-col>
                </b-row>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BInputGroupAppend,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { computed, ref, onMounted } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import axios from "@/libs/axios";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroupAppend,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    tenantOptions: {
      type: Array,
      default: () => [],
    },
    timezoneOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const blankData = {
      id: null,
      name: null,
      id_tenant: null,
      id_timezone: null,
      start_dst: {
        month: null, // 1 - 12
        week: null, // 0, 1, 2, 4
        day: null, // 0,1,2,3,4,5,6
      },
      end_dst: {
        month: null, // 1 - 12
        week: null, // 0, 1, 2, 4
        day: null, // 0,1,2,3,4,5,6
      }
    }

    const days = ref([
      {
        label: 'Lunes',
        value: 1
      },
      {
        label: 'Martes',
        value: 2
      },
      {
        label: 'Miercoles',
        value: 3
      },
      {
        label: 'Jueves',
        value: 4
      },
      {
        label: 'Viernes',
        value: 5
      },
      {
        label: 'Sabado',
        value: 6
      },
      {
        label: 'Domingo',
        value: 7
      },
    ]);

    const months = ref([
      {
        label: 'Enero',
        value: 1
      },
      {
        label: 'Febrero',
        value: 2
      },
      {
        label: 'Marzo',
        value: 3
      },
      {
        label: 'Abril',
        value: 4
      },
      {
        label: 'Mayo',
        value: 5
      },
      {
        label: 'Junio',
        value: 6
      },
      {
        label: 'Julio',
        value: 7
      },
      {
        label: 'Agosto',
        value: 8
      },
      {
        label: 'Septiembre',
        value: 9
      },
      {
        label: 'Octubre',
        value: 10
      },
      {
        label: 'Noviembre',
        value: 11
      },
      {
        label: 'Diciembre',
        value: 12
      }
    ]);

    const weeks = ref([
      {
        label: '1ra',
        value: 1
      },
      {
        label: '2da',
        value: 2
      },
      {
        label: '3ra',
        value: 3
      },
      {
        label: '4ta',
        value: 4
      },
      {
        label: 'Ultima',
        value: 0
      }
    ]);

    const eventTypes = ref([]);

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = async () => {
      let method = (props.isAdd) ? 'post' : 'put';
      let url    = (props.isAdd) ? 'v1/custom-timezones' : `v1/custom-timezones/${itemData.value.id}`;

      await axios[method](url, itemData.value)
        .then((response) => {
          if (response.data.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
      }
    };

    const setEventTypes = (value) => {
      const eventType = props.eventDefinitionOptions.find(etd => (etd.value === value));

      itemData.value.event_type = null;

      if (eventType) {
        eventTypes.value = eventType.related_events;
      }
    }

    return {
      days,
      itemData,
      months,
      onSubmit,
      weeks,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      eventTypes
    };
  },
};
</script>
